import { useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { firstProblem, minLen, isPositiveInt } from "../../components/validators";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { AiAnswerResult, EngineOverviewDto, ModelAction } from "../../ipc/types";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

function AiAssistantPanel(): JSX.Element {
  const client = useIpc();
  const [prompt, setPrompt] = useState("");
  const [answer, setAnswer] = useState<AiAnswerResult | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = async (): Promise<void> => {
    if (prompt.trim().length === 0) return;
    setBusy(true); setAnswer(null);
    const res = await client.askAi(prompt.trim());
    setBusy(false);
    setAnswer(res.ok ? res.value : { ok: false, answer: res.error.message, provider: "—" });
  };

  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="ai" size={18} /> Asistente IA</span>}
        subtitle="Consulta real al proveedor configurado; responde con un snapshot en vivo de tu negocio"
        action={answer ? <Badge tone={answer.ok ? "success" : "warning"} dot>{answer.provider}</Badge> : null}
      />
      <CardBody>
        <div className="pd-field pd-form__full">
          <span className="pd-field__label">Tu pregunta</span>
          <textarea
            className="pd-input" style={{ height: 84, paddingTop: 8, resize: "vertical" }}
            value={prompt} placeholder="Ej: ¿Qué categoría de Mercado Libre tiene más demanda en Chile este mes?"
            aria-label="Pregunta para la IA"
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>
        <div className="pd-actions">
          <Button size="sm" icon="spark" onClick={ask} disabled={busy || prompt.trim().length === 0}>
            {busy ? "Consultando…" : "Consultar IA"}
          </Button>
        </div>
        {answer ? (
          <div className="card" style={{ marginTop: "var(--sp-3)", padding: "var(--sp-4)", background: "var(--bg-canvas)" }}>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Respuesta</div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{answer.answer}</div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): ModelAction[] {
  if (statusLabel === "Borrador" || statusLabel === "Obsoleto") return ["activate", "retire"];
  if (statusLabel === "Activo") return ["retire"];
  return [];
}
const ACTION_LABEL: Record<ModelAction, string> = { activate: "Activar", retire: "Retirar" };

function NewModelForm({ onCreate, onCancel, busy }: { onCreate: (v: { name: string; provider: string; contextWindow: number }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("anthropic");
  const [ctx, setCtx] = useState("128000");
  const ctxNum = Number(ctx);
  const hint = firstProblem([
    { ok: minLen(name, 2), msg: "Ingresa el nombre del modelo" },
    { ok: isPositiveInt(ctx), msg: "La ventana de contexto debe ser un entero mayor que 0" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nuevo modelo" subtitle="Se registra en Borrador; actívalo para usarlo" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">Proveedor</span>
            <select className="pd-input" value={provider} aria-label="Proveedor" onChange={(e) => setProvider(e.target.value)}>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
              <option value="ollama">Ollama (local)</option>
            </select></div>
          <div className="pd-field"><span className="pd-field__label">Ventana de contexto</span>
            <input className="pd-input" value={ctx} type="number" min="1" aria-label="Ventana de contexto" onChange={(e) => setCtx(e.target.value)} /></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Nombre del modelo</span>
            <input className="pd-input" value={name} placeholder="Ej: Claude Sonnet 4.5" aria-label="Nombre del modelo" onChange={(e) => setName(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ name: name.trim(), provider, contextWindow: Math.round(ctxNum) })}>
            {busy ? "Registrando…" : "Registrar modelo"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ModelsPanel(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("ai"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { name: string; provider: string; contextWindow: number }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.registerModel(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Modelo "${v.name}" registrado.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo registrar" : res.error.message);
  };
  const act = async (id: string, action: ModelAction): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.setModelStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      <div className="row between wrap" style={{ marginBottom: "var(--sp-4)", gap: "var(--sp-3)" }}>
        <h2 className="strong" style={{ fontSize: 16 }}>Registro de modelos</h2>
        <Button size="sm" icon="ai" onClick={() => setShowForm((s) => !s)}>Nuevo modelo</Button>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewModelForm onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Modelos disponibles" subtitle={`${data.rows.length} modelos`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="ai" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes modelos</div><div className="faint" style={{ fontSize: 13 }}>Registra el primero con “Nuevo modelo”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Modelo</th><th>Proveedor</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "retire" ? "ghost" : "secondary"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

export function AiScreen(): JSX.Element {
  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="ai" size={22} /></div>
            <div>
              <h1 className="pagehead__title">IA</h1>
              <p className="pagehead__sub">Asistente y registro de modelos de IA para tu negocio.</p>
            </div>
          </div>
        </div>
      </div>
      <AiAssistantPanel />
      <ModelsPanel />
    </div>
  );
}
